import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import Sidebar from '../Core/Sidebar';
import Navbar from '../Core/Navbar';
import { 
  Key, 
  Search, 
  Filter, 
  Download, 
  ChevronLeft, 
  ChevronRight,
  Calendar,
  Copy,
  DollarSign,
  Package,
  RefreshCw,
  AlertCircle
} from 'lucide-react';
import '../../Dashboard Style/WalletTransaction.css';
import * as XLSX from 'xlsx';
import { toast } from 'react-toastify';
import { getUnusedBondKeysApi } from '../../Services/Operations/ProductAPI';

const UnusedBondKeys = () => {
  const navigate = useNavigate();
  const authToken = useSelector((state) => state.user?.token);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [bondKeys, setBondKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [perPage, setPerPage] = useState(10);
  const [searchQuery, setSearchQuery] = useState('');
  const [dateFilter, setDateFilter] = useState('all');
  const [originalBondKeys, setOriginalBondKeys] = useState([]);

      const [isMobile, setIsMobile] = useState(false);
  
    // Check screen size on mount and resize
    useEffect(() => {
      const checkMobile = () => {
        const mobile = window.innerWidth < 992;
        setIsMobile(mobile);
  
        // Auto-close sidebar on mobile by default
        if (mobile) {
          setSidebarOpen(true);
        } else {
          setSidebarOpen(true);
        }
      };
  
      checkMobile();
      window.addEventListener('resize', checkMobile);
      return () => window.removeEventListener('resize', checkMobile);
    }, []);
  
  
  
    const closeSidebar = () => {
      if (isMobile) {
        setSidebarOpen(false);
      }
    };


  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  useEffect(() => {
    const style = document.createElement('style');
    style.innerHTML = `
      .sidebar-body {
        height: calc(100vh - 70px);
        overflow-y: auto;
      }
      
      .sidebar-body::-webkit-scrollbar {
        width: 6px;
      }
      
      .sidebar-body::-webkit-scrollbar-track {
        background: rgba(255, 255, 255, 0.05);
        border-radius: 10px;
      }
      
      .sidebar-body::-webkit-scrollbar-thumb {
        background: rgba(255, 255, 255, 0.2);
        border-radius: 10px;
      }
      
      .sidebar-body::-webkit-scrollbar-thumb:hover {
        background: rgba(255, 255, 255, 0.3);
      }
      
      .sidebar .nav-dropdown {
        list-style: none;
        padding-left: 0;
        max-height: 0;
        overflow: hidden;
        transition: max-height 0.3s ease;
      }
      
      .sidebar .nav-dropdown.show {
        max-height: 500px;
      }
      
      .sidebar .nav-dropdown-item {
        padding-left: 2.5rem;
      }
      
      .nav-item.has-dropdown .nav-link {
        position: relative;
        padding-right: 2.5rem;
      }
      
      .nav-item.has-dropdown .dropdown-icon {
        position: absolute;
        right: 1rem;
        top: 50%;
        transform: translateY(-50%);
        transition: transform 0.3s ease;
      }
      
      .nav-item.has-dropdown .nav-link[aria-expanded="true"] .dropdown-icon {
        transform: translateY(-50%) rotate(180deg);
      }
      
      .nav-item.has-dropdown .nav-dropdown {
        background: rgba(255, 255, 255, 0.05);
        border-left: 3px solid var(--primary-color, #0d6efd);
        margin-left: 1rem;
        border-radius: 0 0 8px 8px;
      }
      
      .nav-item.has-dropdown .nav-dropdown-item .nav-link {
        padding: 0.75rem 1rem 0.75rem 2rem;
        color: rgba(255, 255, 255, 0.8);
      }
      
      .nav-item.has-dropdown .nav-dropdown-item .nav-link:hover {
        background: rgba(255, 255, 255, 0.1);
        color: white;
      }
      
      .nav-item.has-dropdown .link-title.nav-item.nav-category {
        font-weight: 600;
        font-size: 0.875rem;
        text-transform: uppercase;
        letter-spacing: 0.5px;
      }
      
      .sidebar {
        transition: all 0.3s ease;
      }
    `;
    document.head.appendChild(style);

    return () => {
      document.head.removeChild(style);
    };
  }, []);
  // Initialize sidebar functionality
  useEffect(() => {
    const sidebar = document.querySelector('.sidebar');
    const sidebarBody = document.querySelector('.sidebar-body');
    
    if (sidebar) {
      if (sidebarOpen) {
        sidebar.classList.remove('close');
      } else {
        sidebar.classList.add('close');
      }
    }
    
    if (sidebarBody) {
      sidebarBody.style.height = 'calc(100vh - 70px)';
      sidebarBody.style.overflowY = 'auto';
    }

    return () => {};
  }, [sidebarOpen]);

  useEffect(() => {
    const style = document.createElement('style');
    style.innerHTML = `
      .sidebar-body {
        height: calc(100vh - 70px);
        overflow-y: auto;
      }
      
      .sidebar-body::-webkit-scrollbar {
        width: 6px;
      }
      
      .sidebar-body::-webkit-scrollbar-track {
        background: rgba(255, 255, 255, 0.05);
        border-radius: 10px;
      }
      
      .sidebar-body::-webkit-scrollbar-thumb {
        background: rgba(255, 255, 255, 0.2);
        border-radius: 10px;
      }
      
      .sidebar-body::-webkit-scrollbar-thumb:hover {
        background: rgba(255, 255, 255, 0.3);
      }
    `;
    document.head.appendChild(style);

    return () => {
      document.head.removeChild(style);
    };
  }, []);

  const fetchUnusedBondKeys = async () => {
    try {
      setLoading(true);
      setError('');

      const result = await getUnusedBondKeysApi(authToken, currentPage, perPage);
      
      if (result.status) {
        const fetchedBondKeys = result.data || [];
        setOriginalBondKeys(fetchedBondKeys);
        setBondKeys(fetchedBondKeys);
        
        if (result.pagination) {
          setTotalPages(result.pagination.last_page || 1);
          setTotalItems(result.pagination.total || fetchedBondKeys.length);
        } else {
          setTotalPages(1);
          setTotalItems(fetchedBondKeys.length);
        }
        
        // toast.success('Bond keys loaded successfully');
      } else {
        const errorMsg = result.message || 'Failed to fetch bond keys';
        setError(errorMsg);
        toast.error(errorMsg);
        
        // Handle session expiration
        if (errorMsg.includes('Session expired') || errorMsg.includes('Unauthorized')) {
          setTimeout(() => {
            navigate('/signin');
          }, 2000);
        }
      }
    } catch (error) {
      console.error('Error fetching bond keys:', error);
      const errorMessage = 'Failed to load bond keys';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (authToken) {
      fetchUnusedBondKeys();
    }
  }, [currentPage, perPage, authToken]);

  const filteredBondKeys = useMemo(() => {
    let filtered = [...originalBondKeys];

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(bondKey => 
        bondKey.transaction_id?.toString().toLowerCase().includes(query) ||
        bondKey.key?.toLowerCase().includes(query) ||
        bondKey.id?.toString().includes(query) ||
        bondKey.key_amount?.toString().includes(query) ||
        bondKey.quantity?.toString().includes(query)
      );
    }

    if (dateFilter !== 'all') {
      const now = new Date();
      filtered = filtered.filter(bondKey => {
        const bondKeyDate = new Date(bondKey.created_at);
        
        switch (dateFilter) {
          case 'today':
            return bondKeyDate.toDateString() === now.toDateString();
          
          case 'week': {
            const oneWeekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
            return bondKeyDate >= oneWeekAgo;
          }
          
          case 'month': {
            const oneMonthAgo = new Date(now.getFullYear(), now.getMonth() - 1, now.getDate());
            return bondKeyDate >= oneMonthAgo;
          }
          
          case 'year': {
            const oneYearAgo = new Date(now.getFullYear() - 1, now.getMonth(), now.getDate());
            return bondKeyDate >= oneYearAgo;
          }
          
          default:
            return true;
        }
      });
    }

    return filtered;
  }, [originalBondKeys, searchQuery, dateFilter]);

  const handleSearch = (e) => {
    e.preventDefault();
  };

  const handleCopyKey = (key) => {
    navigator.clipboard.writeText(key);
    toast.success('Bond key copied to clipboard!');
  };

  const handleDownload = () => {
    if (filteredBondKeys.length === 0) {
      toast.warning('No data to export');
      return;
    }

    const exportData = filteredBondKeys.map(bondKey => ({
      'ID': bondKey.id,
      'Transaction ID': bondKey.transaction_id,
      'Bond Key': bondKey.key,
      'Key Amount': `$${bondKey.key_amount || 0}`,
      'Quantity': bondKey.quantity,
      'Status': bondKey.status === 0 ? 'Unused' : bondKey.status === 1 ? 'Used' : 'Unknown',
      'Created At': formatDate(bondKey.created_at),
      'Used At': bondKey.used_at ? formatDate(bondKey.used_at) : 'Not Used',
      'From User ID': bondKey.from_user_id || 'N/A'
    }));

    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Unused Bond Keys');
    
    const timestamp = new Date().toISOString().split('T')[0];
    const filename = `Unused_Bond_Keys_${timestamp}.xlsx`;
    
    XLSX.writeFile(wb, filename);
    toast.success('Bond keys exported successfully');
  };

  const handleDateFilterChange = (filter) => {
    setDateFilter(filter);
    setCurrentPage(1);
    toast.info(`Date filter changed to ${filter === 'all' ? 'All Time' : filter}`);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status) => {
    switch(status) {
      case 0:
        return <span className="badge badge-warning">Unused</span>;
      case 1:
        return <span className="badge badge-success">Used</span>;
      default:
        return <span className="badge badge-secondary">Unknown</span>;
    }
  };

  const totalKeys = useMemo(() => {
    // filteredBondKeys.reduce((sum, bond) => sum + (bond.quantity || 1), 0),
   return filteredBondKeys.length;
  },
    [filteredBondKeys]
  );
  
  const totalValue = useMemo(() => 
     filteredBondKeys.reduce((sum, bond) => sum + (parseFloat(bond.key_amount || 0) * (bond.quantity || 1)), 0), 
    [filteredBondKeys]
  );
  
  const averageValue = useMemo(() => 
    totalKeys > 0 ? totalValue / totalKeys : 0, 
    [totalKeys, totalValue]
  );

  // Calculate start and end index for current page
  const startIndex = (currentPage - 1) * perPage + 1;
  const endIndex = Math.min(currentPage * perPage, totalItems);

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  const handlePerPageChange = (newPerPage) => {
    setPerPage(newPerPage);
    setCurrentPage(1);
    toast.info(`Displaying ${newPerPage} items per page`);
  };

  const handleRefresh = () => {
    fetchUnusedBondKeys();
    toast.info('Refreshing bond keys...');
  };

  return (
    <div className="main-wrapper dark-theme">
      <nav className={`sidebar ${sidebarOpen ? '' : 'close'}`}>
        <div className="sidebar-header">
          <a href="#" className="sidebar-brand">
            <i className="me-2" data-lucide="atom"></i>
            Willow <span>Quantum</span>
          </a>
          <div className="sidebar-toggler" onClick={toggleSidebar}>
            <span></span>
            <span></span>
            <span></span>
          </div>
        </div>
        <Sidebar
                          isOpen={!sidebarOpen}
                          onClose={closeSidebar}
                        />
              
                        {/* Mobile backdrop */}
                        {isMobile && sidebarOpen && (
                          <div
                            className="sidebar-backdrop"
                            onClick={closeSidebar}
                          />
                        )}
             </nav>
       
             <div className="page-wrapper">
               <Navbar toggleSidebar={toggleSidebar}  isSidebarOpen={sidebarOpen}
                   isMobile={isMobile}/>
       

        <div 
          className="page-content bond-keys-page"
          style={{
            background: 'linear-gradient(to bottom right, rgb(20, 20, 20) 0%, #021e18 25%, rgb(20, 20, 20) 100%)',
            minHeight: '100vh'
          }}
        >
          <div className="container-fluid py-4">
            <div className="row mb-4">
              <div className="col-12">
                <div className="d-flex align-items-center justify-content-between mb-4">
                  <div>
                    <h1 className="h3 mb-1 text-white">Unused Bond Keys</h1>
                    <p className="text-white-50 mb-0">Manage and view your unused bond keys</p>
                  </div>
                  <button 
                    className="btn btn-outline-light"
                    onClick={handleRefresh}
                    disabled={loading}
                  >
                    <RefreshCw size={16} className="me-2" />
                    Refresh
                  </button>
                </div>
              </div>
            </div>

            <div className="row mb-4">
              <div className="col-md-3">
                <div className="stat-card">
                  <div className="stat-icon bond-icon">
                    <Key className="text-warning" size={24} />
                  </div>
                  <div className="stat-content">
                    <div className="stat-label">Total Keys</div>
                    <div className="stat-value">{totalKeys}</div>
                  </div>
                </div>
              </div>
              <div className="col-md-3">
                <div className="stat-card">
                  <div className="stat-icon value-icon">
                    <DollarSign className="text-success" size={24} />
                  </div>
                  <div className="stat-content">
                    <div className="stat-label">Total Value</div>
                    <div className="stat-value">${totalValue.toFixed(2)}</div>
                  </div>
                </div>
              </div>
              <div className="col-md-3">
                <div className="stat-card">
                  <div className="stat-icon avg-icon">
                    <Package className="text-info" size={24} />
                  </div>
                  <div className="stat-content">
                    <div className="stat-label">Average Value</div>
                    <div className="stat-value">${averageValue.toFixed(2)}</div>
                  </div>
                </div>
              </div>
              <div className="col-md-3">
                <div className="stat-card">
                  <div className="stat-icon unused-icon">
                    <AlertCircle className="text-primary" size={24} />
                  </div>
                  <div className="stat-content">
                    <div className="stat-label">Unused Items</div>
                    <div className="stat-value">{filteredBondKeys.length}</div>
                  </div>
                </div>
              </div>
            </div>

            <div className="row mb-4">
              <div className="col-md-8">
                <div className="d-flex gap-3 search-filter">
                  <form onSubmit={handleSearch} className="flex-grow-1">
                    <div className="search-box">
                      <Search size={18} className="search-icon" />
                      <input
                        type="text"
                        className="search-input"
                        placeholder="Search by key, transaction ID, or amount..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        disabled={loading}
                      />
                    </div>
                  </form>
                  <div className="dropdown">
                    <button
                      className="btn btn-outline-light dropdown-toggle"
                      type="button"
                      data-bs-toggle="dropdown"
                      disabled={loading}
                    >
                      <Filter size={16} className="me-2" />
                      {dateFilter === 'all' ? 'Filter by Date' : dateFilter.charAt(0).toUpperCase() + dateFilter.slice(1)}
                    </button>
                    <div className="dropdown-menu">
                      <button 
                        className={`dropdown-item ${dateFilter === 'today' ? 'active' : ''}`}
                        onClick={() => handleDateFilterChange('today')}
                      >
                        Today
                      </button>
                      <button 
                        className={`dropdown-item ${dateFilter === 'week' ? 'active' : ''}`}
                        onClick={() => handleDateFilterChange('week')}
                      >
                        This Week
                      </button>
                      <button 
                        className={`dropdown-item ${dateFilter === 'month' ? 'active' : ''}`}
                        onClick={() => handleDateFilterChange('month')}
                      >
                        This Month
                      </button>
                      <button 
                        className={`dropdown-item ${dateFilter === 'year' ? 'active' : ''}`}
                        onClick={() => handleDateFilterChange('year')}
                      >
                        This Year
                      </button>
                      <div className="dropdown-divider"></div>
                      <button 
                        className={`dropdown-item ${dateFilter === 'all' ? 'active' : ''}`}
                        onClick={() => handleDateFilterChange('all')}
                      >
                        All Time
                      </button>
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-md-4">
                <div className="d-flex export-page gap-2">
                  <button 
                    className="btn btn-outline-light"
                    onClick={handleDownload}
                    disabled={filteredBondKeys.length === 0 || loading}
                  >
                    <Download size={16} className="me-2" />
                    Export ({filteredBondKeys.length})
                  </button>
                  <div className="dropdown">
                    <button
                      className="btn btn-outline-light dropdown-toggle"
                      type="button"
                      data-bs-toggle="dropdown"
                      disabled={loading}
                    >
                      {perPage} per page
                    </button>
                    <div className="dropdown-menu">
                      {[10, 15, 25, 50, 100].map((num) => (
                        <button
                          key={num}
                          className={`dropdown-item ${perPage === num ? 'active' : ''}`}
                          onClick={() => handlePerPageChange(num)}
                        >
                          {num} per page
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="row">
              <div className="col-12">
                <div className="transaction-table-wrapper">
                  {loading ? (
                    <div className="text-center py-5">
                      <div className="spinner-border text-light" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </div>
                      <p className="text-white mt-3">Loading bond keys...</p>
                    </div>
                  ) : error ? (
                    <div className="alert alert-danger text-center">
                      <p className="mb-0">{error}</p>
                      <button 
                        className="btn btn-sm btn-outline-light mt-2"
                        onClick={fetchUnusedBondKeys}
                      >
                        <RefreshCw size={14} className="me-1" /> Try Again
                      </button>
                    </div>
                  ) : filteredBondKeys.length === 0 ? (
                    <div className="text-center py-5">
                      <Key size={48} className="text-white-50 mb-3" />
                      <p className="text-white-50">
                        {searchQuery || dateFilter !== 'all' 
                          ? 'No bond keys found matching your search criteria.' 
                          : 'No unused bond keys found.'}
                      </p>
                      {(searchQuery || dateFilter !== 'all') && (
                        <button 
                          className="btn btn-outline-light mt-2"
                          onClick={() => {
                            setSearchQuery('');
                            setDateFilter('all');
                            toast.info('Filters cleared');
                          }}
                        >
                          Clear Filters
                        </button>
                      )}
                    </div>
                  ) : (
                    <>
                      <div className="table-responsive">
                        <table className="transaction-table">
                          <thead>
                            <tr>
                              <th>ID</th>
                              {/* <th>Transaction ID</th> */}
                              <th>Bond Key</th>
                              <th>Key Amount</th>
                              <th>Slot No</th>
                              <th>Status</th>
                              <th>Created At</th>
                              <th>Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {filteredBondKeys.map((bondKey) => (
                              <tr key={bondKey.id}>
                                <td>
                                  <div className="bond-id">
                                    #{bondKey.id}
                                  </div>
                                </td>
                                {/* <td>
                                  <div className="transaction-id">
                                    {bondKey.transaction_id}
                                  </div>
                                </td> */}
                                <td>
                                  <div className="bond-key-value">
                                    <code className="">
                                      {bondKey.key?.substring(0, 15)}...
                                    </code>
                                  </div>
                                </td>
                                <td>
                                  <div className="amount amount-credit">
                                    ${parseFloat(bondKey.key_amount || 0).toFixed(2)}
                                  </div>
                                </td>
                                <td>
                                  <div className="bond-quantity">
                                    {bondKey.quantity || 1}
                                  </div>
                                </td>
                                <td>
                                  {getStatusBadge(bondKey.status)}
                                </td>
                                <td>
                                  <div className="bond-date">
                                    <Calendar size={14} className="me-2" />
                                    {formatDate(bondKey.created_at)}
                                  </div>
                                </td>
                                <td>
                                  <div className="bond-actions">
                                    <button
                                      className="btn btn-sm btn-outline-light"
                                      onClick={() => handleCopyKey(bondKey.key)}
                                      title="Copy Key"
                                    >
                                      <Copy size={14} />
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>

            {/* Pagination Controls */}
            {!loading && filteredBondKeys.length > 0 && (
              <div className="row mt-4">
                <div className="col-12">
                  <div className="d-flex justify-content-between align-items-center pagination-controls">
                    <div className="page-info text-white-50">
                      Showing {startIndex} to {endIndex} of {totalItems} entries
                    </div>
                    
                    <div className="d-flex gap-2 align-items-center">
                      <button
                        className="btn btn-outline-light pagination-btn"
                        onClick={() => handlePageChange(currentPage - 1)}
                        disabled={currentPage === 1 || loading}
                      >
                        <ChevronLeft size={16} className="me-1" />
                        Previous
                      </button>
                      
                      <div className="d-flex gap-1 page-numbers">
                        {(() => {
                          const pages = [];
                          const maxVisible = 5;
                          let startPage = Math.max(1, currentPage - Math.floor(maxVisible / 2));
                          let endPage = Math.min(totalPages, startPage + maxVisible - 1);
                          
                          if (endPage - startPage + 1 < maxVisible) {
                            startPage = Math.max(1, endPage - maxVisible + 1);
                          }
                          
                          for (let i = startPage; i <= endPage; i++) {
                            pages.push(
                              <button
                                key={i}
                                className={`btn ${currentPage === i ? 'btn-primary' : 'btn-outline-light'}`}
                                onClick={() => handlePageChange(i)}
                                disabled={loading}
                              >
                                {i}
                              </button>
                            );
                          }
                          return pages;
                        })()}
                      </div>
                      
                      <button
                        className="btn btn-outline-light pagination-btn"
                        onClick={() => handlePageChange(currentPage + 1)}
                        disabled={currentPage === totalPages || loading}
                      >
                        Next
                        <ChevronRight size={16} className="ms-1" />
                      </button>
                    </div>
                    
                    <div className="dropdown">
                      <button
                        className="btn btn-outline-light dropdown-toggle"
                        type="button"
                        data-bs-toggle="dropdown"
                        disabled={loading}
                      >
                        Page {currentPage} of {totalPages}
                      </button>
                      <div className="dropdown-menu dropdown-menu-end">
                        <div className="dropdown-header">Go to page</div>
                        {Array.from({ length: Math.min(10, totalPages) }, (_, i) => i + 1).map(page => (
                          <button
                            key={page}
                            className={`dropdown-item ${currentPage === page ? 'active' : ''}`}
                            onClick={() => handlePageChange(page)}
                          >
                            Page {page}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {!loading && filteredBondKeys.length > 0 && (
              <div className="row mt-4">
                <div className="col-md-6">
                  <div className="summary-card">
                    <h5 className="summary-title">Bond Keys Summary</h5>
                    <div className="summary-content">
                      <div className="summary-item">
                        <span className="summary-label">Filtered Items:</span>
                        <span className="summary-value">{filteredBondKeys.length}</span>
                      </div>
                      <div className="summary-item">
                        <span className="summary-label">Total Keys:</span>
                        <span className="summary-value">{totalKeys}</span>
                      </div>
                      <div className="summary-item">
                        <span className="summary-label">Total Value:</span>
                        <span className="summary-value text-success">
                          ${totalValue.toFixed(2)}
                        </span>
                      </div>
                      <div className="summary-item">
                        <span className="summary-label">Average Value:</span>
                        <span className="summary-value text-info">
                          ${averageValue.toFixed(2)}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="info-card">
                    <h5 className="info-title">Filter Information</h5>
                    <div className="info-content">
                      <div className="info-item">
                        <span className="info-label">Date Filter:</span>
                        <span className="info-value">{dateFilter === 'all' ? 'All Time' : dateFilter}</span>
                      </div>
                      <div className="info-item">
                        <span className="info-label">Search Query:</span>
                        <span className="info-value">{searchQuery || 'None'}</span>
                      </div>
                      <div className="info-item">
                        <span className="info-label">Items per Page:</span>
                        <span className="info-value">{perPage}</span>
                      </div>
                      <div className="info-item">
                        <span className="info-label">Current Page:</span>
                        <span className="info-value">{currentPage} of {totalPages}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        <footer className="footer-dark">
          <div className="container">
            <p className="text-center mb-0 text-white">
              Copyright © 2025 Willow Quantum. All rights reserved.
            </p>
          </div>
        </footer>
      </div>
    </div>
  );
};

export default UnusedBondKeys;